<template>
<v-row justify="space-around" align="start">
    <v-col cols="12" sm="7">
        <detail-growth-chart class="mt-3" ref="chart" :chart-data="graphData" :height="500" :scale="scale" />
        <v-row class="px-0 ma-0" align="center" justify="center">
            <v-checkbox v-model="scale" class="pa-0 ma-0 pl-3" label="로그스케일" hide-details />
            <v-checkbox v-model="isSumShown" class="pa-0 ma-0 pl-3" label="합계 표시" hide-details @change="onSumChanged" />
        </v-row>
    </v-col>

    <v-col cols="12" sm="5" v-if="items.length > 0">
        <v-data-table v-model="selected" :headers="headers" :items="items" :sort-by="sort" :sort-desc="sortDesc" :items-per-page="15" item-key="name" multi-sort dense show-select class="elevation-1" @input="onTotalInputChanged">
            <template v-slot:body="{ items }">
                <tbody>
                    <tr v-for="item in items" :key="item.name">
                        <td>
                            <v-checkbox :input-value="visibility(item.name)" :color="itemColor(item)" style="margin:0px; padding:0px" hide-details @change="onCheckboxChanged(item, $event)" />
                        </td>
                        <td :style="`color: ${itemColor(item)}`" style="cursor: pointer" @click="showFinance(item)">
                            <v-row>
                                <span>{{ item.name }}</span>
                                <v-spacer />
                                <v-progress-circular size="16" indeterminate color="primary" v-if="item.loading" />
                            </v-row>
                        </td>
                        <td style="font-size: 10px" v-if="hasSector">
                            {{ sectorData[item.sector] }}
                        </td>
                        <td v-for="key in sort" :key="`${item}-${key}`">
                            {{ item[key] }}
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-data-table>
    </v-col>

    <finance-dialog ref="financeDialog" />
</v-row>
</template>

<script>
import DetailGrowthChart from '@/views/charts/DetailGrowthChart'
import FinanceDialog from '@/views/dialogs/FinanceDialog'
import {
    sectorData
} from '@/constants'

var randomColor = require('randomcolor')

export default {
    name: 'detail-report',
    props: [
        'value', 'interval', 'sort', 'order'
    ],
    components: {
        DetailGrowthChart,
        FinanceDialog
    },
    data() {
        return {
            startDate: '',
            endDate: '',
            names: [],
            codes: [],
            colors: [],
            xAxis: [],
            yAxis: [],
            graphData: {},
            headers: [],
            items: [],
            hasSector: false,
            sortDesc: [],
            selected: [],
            isSumShown: false,
            scale: false
        }
    },
    watch: {
        value() {
            this.updateReport()
        },
    },
    computed: {
        sectorData() {
            return sectorData
        },
        visibility() {
            return (name) => {
                return !this.graphData.datasets.find(x => x.label == name).hidden
            }
        }
    },
    mounted() {
        const splits = this.interval.split("~")
        this.startDate = splits[0].trim()
        this.endDate = splits[1].trim()
        this.updateReport()
    },
    methods: {
        updateReport() {
            const rawData = this.value.map(x => x[1])
            this.initHeaders(rawData[0])
            this.initSortDesc()
            this.initCodeAndNameAndItems(rawData)
            this.xAxis = this.value.map(x => x[0])
            this.initYAxis(rawData)

            this.colors = randomColor({
                count: this.codes.length
            })

            const datasets = []
            for (let i = 0; i < this.names.length; i++) {
                datasets.push({
                    label: `${this.names[i]} (${this.codes[i]})`,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    borderWidth: 1,
                    borderColor: this.colors[i],
                    backgroundColor: this.colors[i],
                    fill: false,
                    data: this.yAxis[i].filter(x => x != 0),
                    hidden: false
                })

                if (i == this.codes.length - 1) datasets[i].hidden = true
            }

            this.graphData = {
                labels: this.xAxis,
                datasets
            }

            this.selected = this.items.slice()
        },
        initHeaders(firstData) {
            this.headers = []

            const rawHeaders = Object.keys(Object.entries(firstData)[0][1])

            this.hasSector = Object.values(firstData).filter(v => v.sector).length > 0

            this.headers.push({
                text: "종 목",
                value: "name",
                sortable: false
            })

            if (this.hasSector) {
                this.headers.push({
                    text: "업 종",
                    value: "sector",
                    sortable: false
                })
            }

            for (const key of this.sort) {
                this.headers.push({
                    text: this.$t(key),
                    value: key
                })
            }
        },
        initSortDesc() {
            this.sortDesc = []
            for (let i = 0; i < this.order.length; i++) {
                this.sortDesc.push(this.order[i] == 'DESC')
            }
        },
        initCodeAndNameAndItems(rawData) {
            this.codes = []
            this.names = []
            this.items = []

            for (const data of rawData) {
                for (const code in data) {
                    const value = data[code]

                    if (!value.name || this.names.includes(value.name)) continue

                    this.codes.push(code)
                    this.names.push(value.name)

                    const item = {}
                    for (const key in value) {
                        if (['amount', 'price'].includes(key)) continue

                        if (key == 'name') {
                            item[key] = `${value.name} (${code})`
                        } else {
                            item[key] = value[key]
                        }

                        item.loading = false
                    }

                    this.items.push(item)
                }
            }

            this.names.push('합 계')
        },
        initYAxis(rawData) {
            this.yAxis = []
            
            for (const code of this.codes) {
                const prices = []
                for (let i = 0; i < rawData.length; i++) {
                    if (rawData[i].hasOwnProperty(code)) prices.push(rawData[i][code].amount)
                    else prices.push(null)
                }
                this.yAxis.push(prices)
            }

            const sums = []
            for (let i = 0; i < this.xAxis.length; i++) {
                let sum = 0
                for (let j = 0; j < this.codes.length; j++) {
                    sum += this.yAxis[j][i]
                }
                sums.push(sum)
            }

            this.codes.push('sum')
            this.yAxis.push(sums)
        },
        onCheckboxChanged(item, value) {
            this.graphData.datasets.find(x => x.label == item.name).hidden = !value
            this.graphData = Object.assign({}, this.graphData)
            if (value) {
                this.selected.push(item)
            } else {
                this.selected.splice(this.selected.indexOf(item), 1)
            }
        },
        onTotalInputChanged() {
            const codes = this.selected.map(s => s.code)

            for (let i = 0; i < this.graphData.datasets.length - 1; i++) {
                let graphCode = this.graphData.datasets[i].label.split("(")[1]
                graphCode = graphCode.substring(0, graphCode.indexOf(")"))
                this.graphData.datasets[i].hidden = !codes.includes(graphCode)
            }
                
            this.graphData = Object.assign({}, this.graphData)
        },
        onSumChanged() {
            this.graphData.datasets[this.graphData.datasets.length - 1].hidden = !this.isSumShown
            this.graphData = Object.assign({}, this.graphData)
        },
        async showFinance(item) {
            item.loading = true
            const code = item.name.substring(item.name.indexOf('(') + 1, item.name.indexOf(')'))

            if (code == '000000') return

            const url = `/finance?code=${code}&date=${this.startDate}`
            const result = await this.$http.get(url)

            item.loading = false
            this.$refs.financeDialog.show(result.data)
        },
        itemColor(item) {
            return this.graphData.datasets.find(x => x.label == item.name).borderColor
        },
    }
}
</script>
